import type { Creative } from './Creative';
import { AppType } from '@/models/enums/AppType';

// Values match the PortalAPI DTOs and the backend proto. They are a wire contract — don't renumber them.

// The apps a playlist can play on. These are AccountManagement's AppType values — the same numbers the App
// table stores to say which apps a location licenses — so "shows on Dual Display" and "licensed for Dual
// Display" are one vocabulary rather than two lists someone has to keep aligned.
//
// Only the apps that can actually play a playlist are offered. The stored type is the full AppType, so adding
// one later means adding it here; the backend rejects anything it can't play.
export const PROMOTION_APP_TYPES = [AppType.DualDisplay, AppType.Tabletop] as const;

export const PlaylistMediaType = {
    Image: 0,
    Video: 1,
} as const;

export type PlaylistMediaType = (typeof PlaylistMediaType)[keyof typeof PlaylistMediaType];

export const PlaylistItemUploadState = {
    Pending: 0,
    Committed: 1,
} as const;

export type PlaylistItemUploadState = (typeof PlaylistItemUploadState)[keyof typeof PlaylistItemUploadState];

// A creative's place in a playlist. The media hangs off the nested creative, because it belongs to the
// shared library rather than to this playlist - the same creative can appear in several, and does not stop
// existing when one is deleted.
export interface PlaylistItem {
    id: string;
    playlistId: string;
    displayOrder: number;
    creative: Creative;
}

export interface Playlist {
    id: string;
    brandId: string;
    name: string;
    description?: string | null;
    apps: AppType[];
    enabled: boolean;
    // Null = inherit from the scoped configuration. Images only; videos play to their end.
    slideshowIntervalSeconds?: number | null;
    items: PlaylistItem[];
    created: string;
    createdBy: string;
    updated?: string | null;
    updatedBy?: string | null;
}

export interface SavePlaylist {
    name: string;
    description?: string | null;
    // Replaces the stored set wholesale — omitting an app removes it.
    apps: AppType[];
    enabled: boolean;
    slideshowIntervalSeconds?: number | null;
}

export interface CreatePlaylist extends SavePlaylist {
    brandId: string;
}

// ----- Upload handshake -----

export interface CreateUploadIntent {
    fileName: string;
    contentType: string;
    sizeBytes: number;
}

export interface UploadIntent {
    itemId: string;
    blobKey: string;
    // Points at Azure Blob Storage, NOT at our API. The browser PUTs the file here directly.
    uploadUri: string;
    expiresOn: string;
}

// No content hash: the backend derives it from what storage recorded, so the browser never has to read the
// whole file just to produce a value the server couldn't verify anyway.
// Short-lived read URL for one item's media. The container is private, so this is the only way to display
// what was uploaded — and it expires, so it's fetched when a preview opens rather than held anywhere.
export interface ItemReadUri {
    readUri: string;
    expiresOn: string;
}

export interface CommitItem {
    width?: number | null;
    height?: number | null;
    durationSeconds?: number | null;
}

// What the media policy allows. Mirrored from PlaylistMediaPolicy in the backend, which re-checks the blob's
// real content type at commit — this is a courtesy to the user, not the enforcement point.
export const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const ALLOWED_VIDEO_TYPES = ['video/mp4'];
export const ALLOWED_MEDIA_TYPES = [...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES];
export const MAX_MEDIA_BYTES = 250 * 1024 * 1024;

export const toggleApp = (apps: AppType[], app: AppType, on: boolean): AppType[] =>
    on ? [...apps.filter((a) => a !== app), app] : apps.filter((a) => a !== app);
